import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styles from './ReviewUserProgress.module.css';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';
import { shortenAddress } from '../../../utils/utils';

import badgeInnovatorSvg from '../../../static_files/badge_innovator.svg';
import badgeAmplifierSvg from '../../../static_files/badge_amplifier.svg';
import badgeExplorerSvg from '../../../static_files/badge_explorer.svg';
import leaderboardGoldSvg from '../../../static_files/leaderboard-gold.svg';
import leaderboardSilverSvg from '../../../static_files/leaderboard-silver.svg';
import leaderboardBronzeSvg from '../../../static_files/leaderboard-bronze.svg';
import leaderboardFallbackIconSvg from '../../../static_files/leaderboard-fallback-icon.svg';

const HISTORY_PAGE_SIZE = 5;
const LEADERBOARD_SKELETON_COUNT = 8;
const HISTORY_SKELETON_COUNT = 6;
const LB_LATE_GRACE_HOURS = 25;
const LB_RECHECK_INTERVAL_MS = 300000;
const LB_STAMP_FMT = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };

const MEMBERSHIP_BADGE_BY_USER_CLASS = {
    'Innovator Member': { icon: badgeInnovatorSvg, label: 'Innovator' },
    'Amplifier Member': { icon: badgeAmplifierSvg, label: 'Amplifier' },
    'Explorer Member': { icon: badgeExplorerSvg, label: 'Explorer' },
};

const MEDAL_ICON_BY_RANK = {
    1: leaderboardGoldSvg,
    2: leaderboardSilverSvg,
    3: leaderboardBronzeSvg,
};

function getMembershipBadge(userClass) {
    return MEMBERSHIP_BADGE_BY_USER_CLASS[userClass] || null;
}

function getValidatorLabel(userRole) {
    const normalizedUserRole = String(userRole || '').trim().toLowerCase();
    if (normalizedUserRole === 'senior qa') return 'Senior Validator';
    if (normalizedUserRole === 'expert qa') return 'Expert Validator';
    return 'Validator';
}

function getLeaderboardDisplayName(entry) {
    if (!entry) return '—';
    if (entry.email) return entry.email;
    if (entry.wallet_address) return entry.wallet_address;
    return '—';
}

function formatNumber(value) {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatNumberOrDash(value) {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatSignedNumber(value) {
    const n = Number(value ?? 0);
    if (!Number.isFinite(n)) return '+0';
    const sign = n > 0 ? '+' : (n < 0 ? '-' : '+');
    return `${sign}${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatAcceptanceRate(value) {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${Math.round(n)}%`;
}

function formatScore(value) {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toFixed(1)}/100`;
}

function scoreDotClass(score) {
    const n = Number(score);
    return Number.isFinite(n) && n >= 85 ? styles.scoreDotGreen : styles.scoreDotNeutral;
}

const STATE_LABEL = { pending: 'Pending', earned: 'Earned', failed: 'Failed' };

function getLeaderboardUpdatedInfo(updatedAtIso, now) {
    if (!updatedAtIso) return null;
    const updatedAt = new Date(updatedAtIso);
    if (Number.isNaN(updatedAt.getTime())) return null;

    const next = new Date(updatedAt.getTime() + 86400000);
    const ageHours = (now - updatedAt.getTime()) / 3600000;
    const isDelayed = ageHours > LB_LATE_GRACE_HOURS;

    return {
        text: `Updated ${updatedAt.toLocaleString(undefined, LB_STAMP_FMT)}`,
        tooltip: `Rankings refresh daily at 00:00 UTC\n`
            + `Next update ${next.toLocaleString(undefined, LB_STAMP_FMT)}\n`
            + `Reviews completed after the last update count toward the next one.`,
        isDelayed,
    };
}

function LeaderboardAvatar({ userClass }) {
    const membershipBadge = getMembershipBadge(userClass);
    return (
        <span className={styles.lbAv}>
            <img
                src={membershipBadge ? membershipBadge.icon : leaderboardFallbackIconSvg}
                alt="Membership badge"
                className={styles.lbAvImg}
            />
        </span>
    );
}

function LeaderboardRow({ entry, rank, isMe }) {
    const hasRank = rank !== null && rank !== undefined && Number.isFinite(Number(rank));
    const medalIcon = hasRank ? MEDAL_ICON_BY_RANK[Number(rank)] : null;
    const rowClass = [styles.lbRow, isMe && styles.lbRowMe].filter(Boolean).join(' ');
    return (
        <div className={rowClass}>
            <div className={styles.lbRank}>
                {medalIcon ? (
                    <img src={medalIcon} alt={`Rank ${rank}`} className={styles.medalImg} />
                ) : (
                    <span className={styles.rnum}>{hasRank ? Number(rank) : '—'}</span>
                )}
            </div>
            <div className={styles.lbUser}>
                <LeaderboardAvatar userClass={entry.user_class} />
                <span className={styles.lbName} title={getLeaderboardDisplayName(entry)}>
                    {getLeaderboardDisplayName(entry)}
                </span>
            </div>
            <span className={styles.lbH}>{formatNumberOrDash(entry.uploads_reviewed)}</span>
            <span className={styles.lbP}>{formatNumber(entry.total_points)}</span>
        </div>
    );
}

function LeaderboardSkeleton() {
    return Array.from({ length: LEADERBOARD_SKELETON_COUNT }).map((_, i) => (
        <div key={i} className={styles.lbRow}>
            <div className={styles.lbRank}><div className={styles.skeletonDot} /></div>
            <div className={styles.lbUser}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonBar} style={{ width: '90px' }} />
            </div>
            <div className={styles.skeletonBar} style={{ width: '40px', marginLeft: 'auto' }} />
            <div className={styles.skeletonBar} style={{ width: '50px', marginLeft: 'auto' }} />
        </div>
    ));
}

function HistorySkeletonRows() {
    return Array.from({ length: HISTORY_SKELETON_COUNT }).map((_, i) => (
        <tr key={i}>
            <td><div className={styles.skeletonBar} style={{ width: '50px' }} /></td>
            <td><div className={styles.skeletonBar} style={{ width: '140px' }} /></td>
            <td><div className={styles.skeletonBar} style={{ width: '110px' }} /></td>
            <td className={styles.numCell}><div className={styles.skeletonBar} style={{ width: '60px', marginLeft: 'auto' }} /></td>
            <td><div className={styles.skeletonBar} style={{ width: '60px' }} /></td>
        </tr>
    ));
}

export default function ReviewUserProgress() {
    const { gatewayToken, userId, userEmail, walletAddress } = useContext(SwapMainAppContext);
    const token = gatewayToken || localStorage.getItem('gatewayToken');

    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState('');

    const [leaderboard, setLeaderboard] = useState([]);
    const [you, setYou] = useState(null);
    const [lbLoading, setLbLoading] = useState(false);
    const [lbError, setLbError] = useState('');
    const [lbUpdatedAt, setLbUpdatedAt] = useState(null);
    const [lbNowTick, setLbNowTick] = useState(() => Date.now());

    const [historyRows, setHistoryRows] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');

    const loadStats = useCallback(async () => {
        if (!token) {
            setStats(null);
            return;
        }
        setStatsLoading(true);
        setStatsError('');
        try {
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/progress-stats`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to load progress stats');
            const result = await response.json();
            setStats(result.data || null);
        } catch (err) {
            console.error('Progress stats error:', err);
            setStatsError('Failed to load your stats.');
        } finally {
            setStatsLoading(false);
        }
    }, [token]);

    const loadLeaderboard = useCallback(async () => {
        setLbLoading(true);
        setLbError('');
        try {
            const url = new URL(`${PRISMAX_DATA_PIPELINE_URL}/data/qa/leaderboard`);
            if (userId !== undefined && userId !== null && userId !== '') {
                url.searchParams.set('userId', String(userId));
            }
            const response = await fetch(url.toString(), { method: 'GET' });
            if (!response.ok) throw new Error('Failed to load leaderboard');
            const result = await response.json();
            setLeaderboard(Array.isArray(result.leaderboard) ? result.leaderboard : []);
            setYou(result.you || null);
            setLbUpdatedAt(result.updated_at || null);
        } catch (err) {
            console.error('Leaderboard error:', err);
            setLbError('Failed to load the leaderboard.');
        } finally {
            setLbLoading(false);
        }
    }, [userId]);

    const loadHistory = useCallback(async (pageNum) => {
        if (!token) {
            setHistoryRows([]);
            return;
        }
        setHistoryLoading(true);
        setHistoryError('');
        try {
            const response = await fetch(
                `${PRISMAX_DATA_PIPELINE_URL}/data/qa/progress-history?page=${pageNum}&page_size=${HISTORY_PAGE_SIZE}`,
                { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error('Failed to load review history');
            const result = await response.json();
            setHistoryRows(result.data || []);
            setHistoryTotal(result.total || 0);
        } catch (err) {
            console.error('Progress history error:', err);
            setHistoryError('Failed to load your review history.');
        } finally {
            setHistoryLoading(false);
        }
    }, [token]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);
    useEffect(() => { loadHistory(historyPage); }, [historyPage, loadHistory]);

    useEffect(() => {
        const interval = setInterval(() => setLbNowTick(Date.now()), LB_RECHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    const lbUpdatedInfo = useMemo(
        () => getLeaderboardUpdatedInfo(lbUpdatedAt, lbNowTick),
        [lbUpdatedAt, lbNowTick]
    );

    const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));

    function handleHistoryPrev() {
        if (historyPage > 1) setHistoryPage(p => p - 1);
    }
    function handleHistoryNext() {
        if (historyPage < historyTotalPages) setHistoryPage(p => p + 1);
    }

    const membershipBadge = getMembershipBadge(stats?.user_class);
    const validatorLabel = getValidatorLabel(stats?.qa_role ?? stats?.user_role);
    const displayName = userEmail || shortenAddress(walletAddress) || 'You';

    const meUserId = userId !== undefined && userId !== null ? String(userId) : null;
    const isSelfInTop = !!(meUserId && leaderboard.some(row => String(row.user_id) === meUserId));
    const showAppendedYou = !!(you && meUserId && String(you.user_id) === meUserId && !isSelfInTop);

    return (
        <div className={styles.wrapper}>
            {/* Top: personal stats */}
            <div className={styles.panelCard}>
                <div className={styles.meHead}>
                    <div className={styles.meAvatar}>
                        {membershipBadge ? (
                            <img src={membershipBadge.icon} alt={membershipBadge.label} className={styles.meAvatarImg} />
                        ) : null}
                    </div>
                    <div className={styles.meId}>
                        <div className={styles.meName}>{displayName}</div>
                        <div className={styles.meBadges}>
                            {stats?.is_validator && (
                                <span className={styles.badgeValidator}>
                                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                        <path d="M7 1l1.7 1.2 2-.1.6 1.9 1.6 1.2-.7 1.9.7 1.9-1.6 1.2-.6 1.9-2-.1L7 13l-1.7-1.2-2 .1-.6-1.9L1.1 8.8l.7-1.9-.7-1.9 1.6-1.2.6-1.9 2 .1z" fill="var(--rup-green)" fillOpacity="0.16" stroke="var(--rup-green)" strokeWidth="1" strokeLinejoin="round" />
                                        <path d="M4.7 7l1.6 1.6L9.4 5.3" stroke="var(--rup-green)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    {validatorLabel}
                                </span>
                            )}
                            {membershipBadge && (
                                <span className={styles.badgeAmp}>
                                    <span className={styles.badgeAmpDot} />
                                    {membershipBadge.label}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className={styles.meRankInline}>
                        <span className={styles.meRankLabel}>Your rank</span>
                        <span className={styles.meRankVal}>
                            <span className={styles.hash}>#</span>{stats?.rank != null ? formatNumber(stats.rank) : '—'}
                        </span>
                    </div>
                </div>

                {statsError && <p className={styles.stateMessage}>{statsError}</p>}

                <div className={styles.statStrip}>
                    <div className={styles.statCell}>
                        <div className={styles.lbl}>Prisma earned</div>
                        <div className={`${styles.val} ${styles.valPrisma}`}>
                            {statsLoading ? <div className={styles.skeletonBar} style={{ width: '70px' }} /> : formatNumber(stats?.prisma_earned)}
                        </div>
                    </div>
                    <div className={styles.statCell}>
                        <div className={styles.lbl}>Uploads reviewed</div>
                        <div className={styles.val}>
                            {statsLoading ? <div className={styles.skeletonBar} style={{ width: '50px' }} /> : formatNumber(stats?.uploads_reviewed)}
                        </div>
                    </div>
                    <div className={styles.statCell}>
                        <div className={styles.lbl}>Acceptance rate</div>
                        <div className={`${styles.val} ${styles.valGreen}`}>
                            {statsLoading ? <div className={styles.skeletonBar} style={{ width: '40px' }} /> : formatAcceptanceRate(stats?.acceptance_rate)}
                        </div>
                    </div>
                    <div className={styles.statCell}>
                        <div className={styles.lbl}>This week</div>
                        <div className={styles.val}>
                            {statsLoading ? <div className={styles.skeletonBar} style={{ width: '60px' }} /> : formatSignedNumber(stats?.points_this_week)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom: leaderboard | history */}
            <div className={styles.bottomGrid}>
                <div className={`${styles.panelCard} ${styles.sideCard}`}>
                    <div className={styles.lbHead}>
                        <div className={styles.lbTitle}>Leaderboard</div>
                        {lbUpdatedInfo && (
                            <span
                                className={`${styles.lbUpdated} ${lbUpdatedInfo.isDelayed ? styles.lbUpdatedDelayed : ''}`}
                                title={lbUpdatedInfo.tooltip}
                            >
                                <span className={styles.upDot} />
                                {lbUpdatedInfo.text}
                            </span>
                        )}
                    </div>
                    <div className={styles.lbColhead}>
                        <span className={styles.chRank}>Rank</span>
                        <span className={styles.chUser}>Validator</span>
                        <span className={styles.chNum}>Uploads</span>
                        <span className={styles.chNum}>Prisma</span>
                    </div>
                    <div className={styles.lbScroll}>
                        {lbLoading && <LeaderboardSkeleton />}
                        {!lbLoading && lbError && <p className={styles.stateMessage}>{lbError}</p>}
                        {!lbLoading && !lbError && leaderboard.length === 0 && (
                            <p className={styles.stateMessage}>No leaderboard data yet.</p>
                        )}
                        {!lbLoading && !lbError && leaderboard.map((entry, idx) => {
                            const rank = entry.rank != null ? Number(entry.rank) : idx + 1;
                            const isMe = !!(meUserId && String(entry.user_id) === meUserId);
                            return (
                                <React.Fragment key={entry.user_id ?? idx}>
                                    <LeaderboardRow entry={entry} rank={rank} isMe={isMe} />
                                    {rank === 3 && <div className={styles.lbSep} />}
                                </React.Fragment>
                            );
                        })}
                        {!lbLoading && !lbError && showAppendedYou && (
                            <LeaderboardRow entry={you} rank={you.rank} isMe />
                        )}
                    </div>
                </div>

                <div className={`${styles.panelCard} ${styles.sideCard}`}>
                    <div className={styles.historyHead}>Review history</div>
                    <div className={styles.histScroll}>
                        <table className={styles.histTable}>
                            <thead>
                                <tr>
                                    <th>Upload ID</th>
                                    <th>Task Name</th>
                                    <th>Review Time</th>
                                    <th className={styles.numCell}>Quality Score</th>
                                    <th>Prisma Points</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyLoading && <HistorySkeletonRows />}
                                {!historyLoading && historyError && (
                                    <tr><td colSpan={5} className={styles.stateCell}>{historyError}</td></tr>
                                )}
                                {!historyLoading && !historyError && historyRows.length === 0 && (
                                    <tr><td colSpan={5} className={styles.stateCell}>No review history found.</td></tr>
                                )}
                                {!historyLoading && !historyError && historyRows.map((row, i) => (
                                    <tr key={`${row.upload_id}-${i}`}>
                                        <td className={styles.idCell}>{row.upload_id}</td>
                                        <td className={styles.taskCell}>{row.scenario}</td>
                                        <td className={styles.timeCell}>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                                        <td className={styles.numCell}>
                                            <span className={styles.scorePill}>
                                                <span className={`${styles.scoreDot} ${scoreDotClass(row.score)}`} />
                                                {formatScore(row.score)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={styles.pointsCell}>
                                                <span className={`${styles.pointsVal} ${styles[`pointsVal${row.state.charAt(0).toUpperCase()}${row.state.slice(1)}`]}`}>
                                                    {row.state === 'failed' ? '—' : formatNumber(row.points)}
                                                </span>
                                                <span className={`${styles.pointsState} ${styles[`pointsState${row.state.charAt(0).toUpperCase()}${row.state.slice(1)}`]}`}>
                                                    {STATE_LABEL[row.state] || row.state}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {!historyLoading && !historyError && historyTotal > 0 && (
                        <div className={styles.histPager}>
                            <button className={styles.pagerBtn} onClick={handleHistoryPrev} disabled={historyPage === 1} aria-label="Previous">
                                &#8592;
                            </button>
                            <span className={styles.pagerInfo}>{historyPage} / {historyTotalPages}</span>
                            <button className={styles.pagerBtn} onClick={handleHistoryNext} disabled={historyPage === historyTotalPages} aria-label="Next">
                                &#8594;
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
